import React, { useState, useEffect } from 'react';
import { toast } from 'sonner';
import {
  Plus,
  Search,
  UserCog,
  Edit2,
  Trash2,
  X,
  Power,
  Filter,
  Briefcase,
  Building,
  User,
  AlertCircle,
} from 'lucide-react';
import { authHeaders } from '../utils/api';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const EMPTY_FORM = {
  nome_completo: '',
  cpf: '',
  rg: '',
  data_nascimento: '',
  sexo: '',
  email_pessoal: '',
  telefone_contato: '',
  endereco_completo: '',
  cargo: '',
  departamento: '',
  data_admissao: '',
  data_demissao: '',
  tipo_contrato: '',
  salario_base: '',
  valor_hora: '',
  valor_hora_servico: '',
  observacoes: '',
  user_id: '',
  operator_id: '',
  e_operador: false,  // Bloco 9 Correção 3
};

const formatCPF = (digits) => {
  if (!digits) return '';
  const d = String(digits).replace(/\D/g, '').slice(0, 11);
  if (d.length <= 3) return d;
  if (d.length <= 6) return `${d.slice(0, 3)}.${d.slice(3)}`;
  if (d.length <= 9) return `${d.slice(0, 3)}.${d.slice(3, 6)}.${d.slice(6)}`;
  return `${d.slice(0, 3)}.${d.slice(3, 6)}.${d.slice(6, 9)}-${d.slice(9)}`;
};

const formatCurrency = (v) => {
  if (v === null || v === undefined || v === '') return '-';
  return Number(v).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
};

export default function Colaboradores({ userRole = 'admin' }) {
  const canEdit = ['admin'].includes(userRole);

  const [colabs, setColabs] = useState([]);
  const [users, setUsers] = useState([]);
  const [operators, setOperators] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('ativos');

  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const tc = form.tipo_contrato;
  const showSalarioBase = !tc || tc === 'Mensalista' || tc === 'CLT' || tc === 'Misto';
  const showValorHora = !tc || tc === 'Horista Puro' || tc === 'Misto';
  const [saving, setSaving] = useState(false);
  // Bloco 9 Correção 3 (Fev/2026)
  const [similarAlert, setSimilarAlert] = useState(null);
  const [showDupPanel, setShowDupPanel] = useState(false);

  useEffect(() => {
    fetchAll();
  }, []);

  useEffect(() => {
    const delay = setTimeout(() => fetchColabs(), 300);
    return () => clearTimeout(delay);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search, statusFilter]);

  const fetchAll = async () => {
    setLoading(true);
    try {
      const [cRes, uRes, oRes] = await Promise.all([
        fetch(`${API_URL}/api/colaboradores?status_filter=${statusFilter}${search ? `&search=${encodeURIComponent(search)}` : ''}`, { headers: authHeaders() }),
        fetch(`${API_URL}/api/users`, { headers: authHeaders() }),
        fetch(`${API_URL}/api/operators`, { headers: authHeaders() }),
      ]);
      if (cRes.ok) setColabs(await cRes.json());
      if (uRes.ok) setUsers(await uRes.json());
      if (oRes.ok) setOperators(await oRes.json());
    } catch (e) {
      toast.error('Erro ao carregar dados');
    } finally {
      setLoading(false);
    }
  };

  const fetchColabs = async () => {
    try {
      const res = await fetch(
        `${API_URL}/api/colaboradores?status_filter=${statusFilter}${search ? `&search=${encodeURIComponent(search)}` : ''}`,
        { headers: authHeaders() }
      );
      if (res.ok) setColabs(await res.json());
    } catch {}
  };

  const openNew = () => {
    setEditing(null);
    setForm(EMPTY_FORM);
    setShowModal(true);
  };

  const openEdit = (c) => {
    setEditing(c);
    setForm({
      nome_completo: c.nome_completo || '',
      cpf: c.cpf ? formatCPF(c.cpf) : '',
      rg: c.rg || '',
      data_nascimento: c.data_nascimento || '',
      sexo: c.sexo || '',
      email_pessoal: c.email_pessoal || '',
      telefone_contato: c.telefone_contato || '',
      endereco_completo: c.endereco_completo || '',
      cargo: c.cargo || '',
      departamento: c.departamento || '',
      data_admissao: c.data_admissao || '',
      data_demissao: c.data_demissao || '',
      tipo_contrato: c.tipo_contrato || '',
      salario_base: c.salario_base ?? '',
      valor_hora: c.valor_hora ?? '',
      valor_hora_servico: c.valor_hora_servico ?? '',
      observacoes: c.observacoes || '',
      user_id: c.user_id || '',
      operator_id: c.operator_id || '',
      e_operador: c.e_operador || false,
    });
    setShowModal(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.nome_completo || form.nome_completo.trim().length < 2) {
      toast.error('Nome completo é obrigatório (mínimo 2 caracteres)');
      return;
    }

    setSaving(true);
    try {
      const payload = {
        ...form,
        cpf: form.cpf ? form.cpf.replace(/\D/g, '') : null,
        tipo_contrato: form.tipo_contrato === '' ? null : form.tipo_contrato,
        salario_base: form.salario_base === '' ? null : Number(form.salario_base),
        valor_hora: form.valor_hora === '' ? null : Number(form.valor_hora),
        valor_hora_servico: form.valor_hora_servico === '' ? null : Number(form.valor_hora_servico),
      };
      // remover chaves vazias para opcionais
      Object.keys(payload).forEach((k) => {
        if (payload[k] === '' || payload[k] === undefined) payload[k] = null;
      });

      const url = editing
        ? `${API_URL}/api/colaboradores/${editing.id}`
        : `${API_URL}/api/colaboradores`;
      const method = editing ? 'PUT' : 'POST';

      const res = await fetch(url, {
        method,
        headers: { ...authHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });

      if (res.ok) {
        toast.success(editing ? 'Colaborador atualizado' : 'Colaborador cadastrado');
        setShowModal(false);
        await fetchColabs();
      } else if (res.status === 409) {
        // Bloco 9 Correção 3: alerta de operador similar
        const err = await res.json().catch(() => ({}));
        const detail = err.detail || {};
        if (detail.error === 'similar_operator_found') {
          setSimilarAlert({ candidates: detail.candidates || [], payload });
        } else {
          toast.error(detail.message || 'Conflito ao salvar');
        }
      } else {
        const err = await res.json().catch(() => ({}));
        toast.error(err.detail || 'Erro ao salvar colaborador');
      }
    } catch {
      toast.error('Erro de conexão');
    } finally {
      setSaving(false);
    }
  };

  const handleToggleStatus = async (c) => {
    if (!window.confirm(`${c.ativo ? 'Inativar' : 'Ativar'} o colaborador "${c.nome_completo}"?`)) return;
    try {
      const res = await fetch(`${API_URL}/api/colaboradores/${c.id}/toggle-status`, {
        method: 'PATCH',
        headers: authHeaders(),
      });
      if (res.ok) {
        toast.success(`Colaborador ${c.ativo ? 'inativado' : 'ativado'}`);
        await fetchColabs();
      } else {
        toast.error('Erro ao alterar status');
      }
    } catch {
      toast.error('Erro de conexão');
    }
  };

  const handleDelete = async (c) => {
    if (!window.confirm(`Excluir o colaborador "${c.nome_completo}"? Se houver lançamentos financeiros vinculados, ele será apenas inativado.`)) return;
    try {
      const res = await fetch(`${API_URL}/api/colaboradores/${c.id}`, {
        method: 'DELETE',
        headers: authHeaders(),
      });
      if (res.status === 409) {
        const err = await res.json();
        toast.warning(err.detail || 'Colaborador inativado (há vínculos financeiros)');
        await fetchColabs();
      } else if (res.ok) {
        toast.success('Colaborador excluído');
        await fetchColabs();
      } else {
        const err = await res.json().catch(() => ({}));
        toast.error(err.detail || 'Erro ao excluir');
      }
    } catch {
      toast.error('Erro de conexão');
    }
  };

  return (
    <div className="space-y-6" data-testid="colaboradores-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl lg:text-3xl font-bold text-white">Colaboradores</h1>
          <p className="text-gray-400 mt-1">Cadastro e gestão de funcionários da empresa</p>
        </div>
        {canEdit && (
          <div className="flex items-center gap-2">
            <button
              onClick={() => setShowDupPanel(true)}
              data-testid="open-dup-panel-button"
              className="flex items-center justify-center gap-2 px-3 py-2 bg-amber-500/10 hover:bg-amber-500/20 text-amber-300 border border-amber-500/30 rounded-lg text-sm transition-colors"
              title="Resolver duplicados Colaborador × Operador"
            >
              ⚠ Duplicados
            </button>
            <button
              onClick={openNew}
              data-testid="add-colaborador-button"
              className="flex items-center justify-center gap-2 px-4 py-2 bg-yellow-500 hover:bg-yellow-400 text-black font-semibold rounded-lg transition-colors"
            >
              <Plus size={20} />
              <span>Novo Colaborador</span>
            </button>
          </div>
        )}
      </div>

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-3">
        <div className="relative flex-1">
          <Search size={20} className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-500" />
          <input
            type="text"
            placeholder="Buscar por nome ou CPF..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="w-full pl-12 pr-4 py-3 bg-[#111] border border-gray-800 rounded-lg text-white placeholder-gray-500 focus:border-yellow-500 focus:outline-none transition-colors"
            data-testid="colab-search-input"
          />
        </div>
        <div className="flex gap-1 bg-[#111] border border-gray-800 rounded-lg p-1" data-testid="colab-status-tabs">
          {[['ativos', 'Ativos'], ['inativos', 'Inativos'], ['all', 'Todos']].map(([val, label]) => (
            <button key={val} onClick={() => setStatusFilter(val)}
              data-testid={`colab-tab-${val}`}
              className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                statusFilter === val ? 'bg-yellow-500/20 text-yellow-400 border border-yellow-500/30' : 'text-gray-400 hover:text-white'
              }`}>
              {label}
            </button>
          ))}
        </div>
      </div>

      {/* List */}
      {loading ? (
        <div className="text-center py-12 text-gray-400">Carregando...</div>
      ) : colabs.length === 0 ? (
        <div className="bg-[#111] border border-gray-800 rounded-xl p-10 text-center">
          <UserCog className="mx-auto mb-3 text-gray-600" size={42} />
          <p className="text-white font-semibold">Nenhum colaborador encontrado</p>
          <p className="text-gray-400 text-sm mt-1">Cadastre o primeiro colaborador para começar.</p>
        </div>
      ) : (
        <div className="bg-[#111] border border-gray-800 rounded-xl overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-[#0A0A0A] text-gray-400 uppercase text-xs">
                <tr>
                  <th className="text-left px-4 py-3">Nome</th>
                  <th className="text-left px-4 py-3">CPF</th>
                  <th className="text-left px-4 py-3">Cargo</th>
                  <th className="text-left px-4 py-3">Departamento</th>
                  <th className="text-left px-4 py-3">Vínculos</th>
                  <th className="text-center px-4 py-3">Status</th>
                  {canEdit && <th className="text-right px-4 py-3">Ações</th>}
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-800">
                {colabs.map((c) => (
                  <tr key={c.id} data-testid={`colab-row-${c.id}`} className="hover:bg-[#1a1a1a] transition-colors">
                    <td className="px-4 py-3 text-white font-medium">{c.nome_completo}</td>
                    <td className="px-4 py-3 text-gray-300 font-mono">{c.cpf ? formatCPF(c.cpf) : '-'}</td>
                    <td className="px-4 py-3 text-gray-300">{c.cargo || '-'}</td>
                    <td className="px-4 py-3 text-gray-300">{c.departamento || '-'}</td>
                    <td className="px-4 py-3 text-xs">
                      <div className="flex flex-col gap-0.5">
                        {c.user_info && <span className="text-blue-400">👤 {c.user_info.name}</span>}
                        {c.operator_info && <span className="text-yellow-400">🚜 {c.operator_info.name}</span>}
                        {!c.user_info && !c.operator_info && <span className="text-gray-600">-</span>}
                      </div>
                    </td>
                    <td className="px-4 py-3 text-center">
                      <span
                        className={`inline-block px-2 py-1 rounded text-xs font-semibold ${
                          c.ativo
                            ? 'bg-green-500/15 text-green-400 border border-green-500/30'
                            : 'bg-gray-500/15 text-gray-400 border border-gray-500/30'
                        }`}
                      >
                        {c.ativo ? 'Ativo' : 'Inativo'}
                      </span>
                    </td>
                    {canEdit && (
                      <td className="px-4 py-3">
                        <div className="flex justify-end gap-1">
                          <button
                            onClick={() => openEdit(c)}
                            className="p-2 bg-gray-800 hover:bg-gray-700 text-gray-300 rounded-lg transition-colors"
                            data-testid={`edit-colab-${c.id}`}
                            title="Editar"
                          >
                            <Edit2 size={16} />
                          </button>
                          <button
                            onClick={() => handleToggleStatus(c)}
                            className={`p-2 ${
                              c.ativo
                                ? 'bg-orange-500/15 hover:bg-orange-500/25 text-orange-400'
                                : 'bg-green-500/15 hover:bg-green-500/25 text-green-400'
                            } rounded-lg transition-colors`}
                            data-testid={`toggle-colab-${c.id}`}
                            title={c.ativo ? 'Inativar' : 'Ativar'}
                          >
                            <Power size={16} />
                          </button>
                          <button
                            onClick={() => handleDelete(c)}
                            className="p-2 bg-red-500/15 hover:bg-red-500/25 text-red-400 rounded-lg transition-colors"
                            data-testid={`delete-colab-${c.id}`}
                            title="Excluir"
                          >
                            <Trash2 size={16} />
                          </button>
                        </div>
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4">
          <div className="bg-[#111] border border-gray-800 rounded-xl w-full max-w-3xl max-h-[92vh] overflow-y-auto">
            <div className="flex items-center justify-between p-4 lg:p-6 border-b border-gray-800">
              <h2 className="text-xl font-semibold text-white">
                {editing ? 'Editar Colaborador' : 'Novo Colaborador'}
              </h2>
              <button
                onClick={() => setShowModal(false)}
                className="p-2 text-gray-400 hover:text-white transition-colors"
                data-testid="close-colab-modal"
              >
                <X size={20} />
              </button>
            </div>

            <form onSubmit={handleSubmit} className="p-4 lg:p-6 space-y-4">
              {/* Bloco: Dados Pessoais */}
              <div className="bg-[#0A0A0A] border border-gray-800 rounded-lg p-4 space-y-3">
                <div className="flex items-center gap-2 text-yellow-500 text-sm font-semibold">
                  <User size={16} /> Dados Pessoais
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <div className="md:col-span-2">
                    <label className="block text-xs text-gray-400 mb-1">Nome Completo *</label>
                    <input
                      type="text"
                      value={form.nome_completo}
                      onChange={(e) => setForm({ ...form, nome_completo: e.target.value })}
                      required
                      className="w-full px-3 py-2 bg-[#111] border border-gray-800 rounded-lg text-white focus:border-yellow-500 focus:outline-none"
                      data-testid="input-nome-completo"
                    />
                  </div>
                  <div>
                    <label className="block text-xs text-gray-400 mb-1">CPF</label>
                    <input
                      type="text"
                      value={form.cpf}
                      onChange={(e) => setForm({ ...form, cpf: formatCPF(e.target.value) })}
                      placeholder="000.000.000-00"
                      className="w-full px-3 py-2 bg-[#111] border border-gray-800 rounded-lg text-white focus:border-yellow-500 focus:outline-none"
                      data-testid="input-cpf"
                    />
                  </div>
                  <div>
                    <label className="block text-xs text-gray-400 mb-1">RG</label>
                    <input
                      type="text"
                      value={form.rg}
                      onChange={(e) => setForm({ ...form, rg: e.target.value })}
                      className="w-full px-3 py-2 bg-[#111] border border-gray-800 rounded-lg text-white focus:border-yellow-500 focus:outline-none"
                      data-testid="input-rg"
                    />
                  </div>
                  <div>
                    <label className="block text-xs text-gray-400 mb-1">Data de Nascimento</label>
                    <input
                      type="date"
                      value={form.data_nascimento}
                      onChange={(e) => setForm({ ...form, data_nascimento: e.target.value })}
                      className="w-full px-3 py-2 bg-[#111] border border-gray-800 rounded-lg text-white focus:border-yellow-500 focus:outline-none"
                      data-testid="input-data-nascimento"
                    />
                  </div>
                  <div>
                    <label className="block text-xs text-gray-400 mb-1">Sexo</label>
                    <select
                      value={form.sexo}
                      onChange={(e) => setForm({ ...form, sexo: e.target.value })}
                      className="w-full px-3 py-2 bg-[#111] border border-gray-800 rounded-lg text-white focus:border-yellow-500 focus:outline-none"
                      data-testid="input-sexo"
                    >
                      <option value="">-</option>
                      <option value="Masculino">Masculino</option>
                      <option value="Feminino">Feminino</option>
                      <option value="Outro">Outro</option>
                    </select>
                  </div>
                </div>
              </div>

              {/* Bloco: Contato */}
              <div className="bg-[#0A0A0A] border border-gray-800 rounded-lg p-4 space-y-3">
                <div className="flex items-center gap-2 text-yellow-500 text-sm font-semibold">
                  <Briefcase size={16} /> Contato
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <div>
                    <label className="block text-xs text-gray-400 mb-1">E-mail Pessoal</label>
                    <input
                      type="email"
                      value={form.email_pessoal}
                      onChange={(e) => setForm({ ...form, email_pessoal: e.target.value })}
                      className="w-full px-3 py-2 bg-[#111] border border-gray-800 rounded-lg text-white focus:border-yellow-500 focus:outline-none"
                      data-testid="input-email-pessoal"
                    />
                  </div>
                  <div>
                    <label className="block text-xs text-gray-400 mb-1">Telefone</label>
                    <input
                      type="tel"
                      value={form.telefone_contato}
                      onChange={(e) => setForm({ ...form, telefone_contato: e.target.value })}
                      placeholder="(00) 00000-0000"
                      className="w-full px-3 py-2 bg-[#111] border border-gray-800 rounded-lg text-white focus:border-yellow-500 focus:outline-none"
                      data-testid="input-telefone"
                    />
                  </div>
                  <div className="md:col-span-2">
                    <label className="block text-xs text-gray-400 mb-1">Endereço Completo</label>
                    <input
                      type="text"
                      value={form.endereco_completo}
                      onChange={(e) => setForm({ ...form, endereco_completo: e.target.value })}
                      className="w-full px-3 py-2 bg-[#111] border border-gray-800 rounded-lg text-white focus:border-yellow-500 focus:outline-none"
                      data-testid="input-endereco"
                    />
                  </div>
                </div>
              </div>

              {/* Bloco: Dados Funcionais */}
              <div className="bg-[#0A0A0A] border border-gray-800 rounded-lg p-4 space-y-3">
                <div className="flex items-center gap-2 text-yellow-500 text-sm font-semibold">
                  <Building size={16} /> Dados Funcionais
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <div>
                    <label className="block text-xs text-gray-400 mb-1">Cargo</label>
                    <input
                      type="text"
                      value={form.cargo}
                      onChange={(e) => setForm({ ...form, cargo: e.target.value })}
                      className="w-full px-3 py-2 bg-[#111] border border-gray-800 rounded-lg text-white focus:border-yellow-500 focus:outline-none"
                      data-testid="input-cargo"
                    />
                    <p className="text-[10px] text-gray-500 mt-1">
                      Cargo contendo "Operador" cria automaticamente o perfil de Operador vinculado.
                    </p>
                  </div>
                  <div>
                    <label className="block text-xs text-gray-400 mb-1">É Operador? <span className="text-gray-500">(força criação)</span></label>
                    <label className="flex items-center gap-2 mt-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={form.e_operador}
                        onChange={(e) => setForm({ ...form, e_operador: e.target.checked })}
                        data-testid="input-e-operador"
                        className="w-4 h-4 text-yellow-500 bg-[#111] border-gray-700 rounded focus:ring-yellow-500"
                      />
                      <span className="text-sm text-gray-300">
                        Forçar criação do perfil de Operador
                      </span>
                    </label>
                  </div>
                  <div>
                    <label className="block text-xs text-gray-400 mb-1">Departamento</label>
                    <input
                      type="text"
                      value={form.departamento}
                      onChange={(e) => setForm({ ...form, departamento: e.target.value })}
                      className="w-full px-3 py-2 bg-[#111] border border-gray-800 rounded-lg text-white focus:border-yellow-500 focus:outline-none"
                      data-testid="input-departamento"
                    />
                  </div>
                  <div>
                    <label className="block text-xs text-gray-400 mb-1">Data de Admissão</label>
                    <input
                      type="date"
                      value={form.data_admissao}
                      onChange={(e) => setForm({ ...form, data_admissao: e.target.value })}
                      className="w-full px-3 py-2 bg-[#111] border border-gray-800 rounded-lg text-white focus:border-yellow-500 focus:outline-none"
                      data-testid="input-data-admissao"
                    />
                  </div>
                  <div>
                    <label className="block text-xs text-gray-400 mb-1">Data de Demissão</label>
                    <input
                      type="date"
                      value={form.data_demissao}
                      onChange={(e) => setForm({ ...form, data_demissao: e.target.value })}
                      className="w-full px-3 py-2 bg-[#111] border border-gray-800 rounded-lg text-white focus:border-yellow-500 focus:outline-none"
                      data-testid="input-data-demissao"
                    />
                  </div>
                  <div>
                    <label className="block text-xs text-gray-400 mb-1">Tipo de Contrato</label>
                    <select
                      value={form.tipo_contrato}
                      onChange={(e) => setForm({ ...form, tipo_contrato: e.target.value })}
                      className="w-full px-3 py-2 bg-[#111] border border-gray-800 rounded-lg text-white focus:border-yellow-500 focus:outline-none"
                      data-testid="select-tipo-contrato"
                    >
                      <option value="">— Não definido —</option>
                      <option value="Mensalista">Mensalista</option>
                      <option value="Horista Puro">Horista Puro</option>
                      <option value="Misto">Misto</option>
                      <option value="CLT">CLT</option>
                    </select>
                    <p className="text-[10px] text-gray-500 mt-1">
                      Define a automação de salário: apenas Mensalista e Misto geram crédito mensal automático.
                    </p>
                  </div>
                </div>
              </div>

              {/* Bloco: Remuneração — campos condicionais por Tipo de Contrato */}
              <div className="bg-[#0A0A0A] border border-gray-800 rounded-lg p-4 space-y-3">
                <div className="flex items-center gap-2 text-yellow-500 text-sm font-semibold">
                  <Building size={16} /> Remuneração
                </div>
                {!form.tipo_contrato && (
                  <p className="text-[11px] text-yellow-500/80" data-testid="hint-tipo-nulo">
                    Cadastro sem Tipo de Contrato definido: exibindo todos os campos de remuneração. Defina o tipo acima para focar os campos relevantes.
                  </p>
                )}
                {form.tipo_contrato === 'CLT' && (
                  <p className="text-[11px] text-gray-400" data-testid="hint-clt">
                    Contratos CLT não geram automação de crédito de salário (reservado para uso futuro).
                  </p>
                )}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  {showSalarioBase && (
                    <div>
                      <label className="block text-xs text-gray-400 mb-1">Salário Base Mensal (R$)</label>
                      <input
                        type="number"
                        step="0.01"
                        min="0"
                        value={form.salario_base}
                        onChange={(e) => setForm({ ...form, salario_base: e.target.value })}
                        className="w-full px-3 py-2 bg-[#111] border border-gray-800 rounded-lg text-white focus:border-yellow-500 focus:outline-none"
                        data-testid="input-salario"
                      />
                      {form.salario_base && (
                        <p className="text-xs text-gray-500 mt-1">{formatCurrency(form.salario_base)}</p>
                      )}
                    </div>
                  )}
                  {showValorHora && (
                    <div>
                      <label className="block text-xs text-gray-400 mb-1">Valor da Hora — O.S. (R$/h)</label>
                      <input
                        type="number"
                        step="0.01"
                        min="0"
                        value={form.valor_hora}
                        onChange={(e) => setForm({ ...form, valor_hora: e.target.value })}
                        className="w-full px-3 py-2 bg-[#111] border border-gray-800 rounded-lg text-white focus:border-yellow-500 focus:outline-none"
                        data-testid="input-valor-hora"
                      />
                      {form.valor_hora && (
                        <p className="text-xs text-gray-500 mt-1">{formatCurrency(form.valor_hora)} / hora</p>
                      )}
                    </div>
                  )}
                  <div>
                    <label className="block text-xs text-gray-400 mb-1">Valor Hora de Serviço (R$/h)</label>
                    <input
                      type="number"
                      step="0.01"
                      min="0"
                      value={form.valor_hora_servico}
                      onChange={(e) => setForm({ ...form, valor_hora_servico: e.target.value })}
                      className="w-full px-3 py-2 bg-[#111] border border-gray-800 rounded-lg text-white focus:border-yellow-500 focus:outline-none"
                      data-testid="input-valor-hora-servico"
                    />
                    <p className="text-[10px] text-gray-500 mt-1">
                      Usado para gerar crédito de diárias/serviço (independente do tipo de contrato).
                    </p>
                  </div>
                </div>
              </div>

              {/* Bloco: Vínculos */}
              <div className="bg-[#0A0A0A] border border-gray-800 rounded-lg p-4 space-y-3">
                <div className="flex items-center gap-2 text-yellow-500 text-sm font-semibold">
                  <AlertCircle size={16} /> Vínculos com o Sistema (opcional)
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <div>
                    <label className="block text-xs text-gray-400 mb-1">Usuário vinculado</label>
                    <select
                      value={form.user_id}
                      onChange={(e) => setForm({ ...form, user_id: e.target.value })}
                      className="w-full px-3 py-2 bg-[#111] border border-gray-800 rounded-lg text-white focus:border-yellow-500 focus:outline-none"
                      data-testid="select-user-id"
                    >
                      <option value="">Nenhum</option>
                      {users.map((u) => (
                        <option key={u.id} value={u.id}>
                          {u.name} ({u.email})
                        </option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-xs text-gray-400 mb-1">Operador de Máquina vinculado</label>
                    <select
                      value={form.operator_id}
                      onChange={(e) => setForm({ ...form, operator_id: e.target.value })}
                      className="w-full px-3 py-2 bg-[#111] border border-gray-800 rounded-lg text-white focus:border-yellow-500 focus:outline-none"
                      data-testid="select-operator-id"
                    >
                      <option value="">Nenhum</option>
                      {operators.map((o) => (
                        <option key={o.id} value={o.id}>
                          {o.name}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
              </div>

              {/* Observações */}
              <div>
                <label className="block text-xs text-gray-400 mb-1">Observações</label>
                <textarea
                  value={form.observacoes}
                  onChange={(e) => setForm({ ...form, observacoes: e.target.value })}
                  rows={3}
                  className="w-full px-3 py-2 bg-[#111] border border-gray-800 rounded-lg text-white focus:border-yellow-500 focus:outline-none resize-none"
                  data-testid="input-observacoes"
                />
              </div>

              {/* Footer */}
              <div className="flex justify-end gap-2 pt-2">
                <button
                  type="button"
                  onClick={() => setShowModal(false)}
                  className="px-4 py-2 text-gray-300 hover:text-white transition-colors"
                  data-testid="cancel-colab-button"
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  disabled={saving}
                  className="px-5 py-2 bg-yellow-500 hover:bg-yellow-400 disabled:opacity-50 text-black font-semibold rounded-lg transition-colors"
                  data-testid="save-colab-button"
                >
                  {saving ? 'Salvando...' : editing ? 'Salvar' : 'Cadastrar'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Bloco 9 Correção 3 (Fev/2026) — Modal de alerta de operador similar */}
      {similarAlert && (
        <div className="fixed inset-0 bg-black/70 z-[60] flex items-center justify-center p-4">
          <div className="bg-[#1A1A1A] border border-amber-500/40 rounded-xl max-w-2xl w-full" data-testid="similar-alert-modal">
            <div className="px-4 py-3 border-b border-gray-800 flex items-center justify-between">
              <h3 className="font-semibold text-amber-400 flex items-center gap-2">⚠ Operador similar encontrado</h3>
              <button onClick={() => setSimilarAlert(null)} className="text-gray-400 hover:text-white">×</button>
            </div>
            <div className="p-4 space-y-3">
              <p className="text-sm text-gray-300">
                Já existe(m) operador(es) com nome/CPF similar(es). Considere vincular ao invés de criar
                um novo registro, evitando duplicidade de Conta Corrente.
              </p>
              <div className="bg-[#0A0A0A] border border-gray-800 rounded-lg overflow-hidden">
                <table className="w-full text-xs">
                  <thead className="bg-gray-900 text-gray-400 uppercase">
                    <tr>
                      <th className="text-left px-3 py-2">Operador existente</th>
                      <th className="text-left px-3 py-2">Documento</th>
                      <th className="text-right px-3 py-2">Score</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(similarAlert.candidates || []).map(c => (
                      <tr key={c.operator_id} className="border-t border-gray-800">
                        <td className="px-3 py-2 text-gray-300">{c.operator_name}</td>
                        <td className="px-3 py-2 text-gray-300 font-mono">{c.operator_document || '—'}</td>
                        <td className={`px-3 py-2 text-right font-mono ${c.score >= 90 ? 'text-rose-400' : 'text-amber-400'}`}>
                          {c.score}%
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <p className="text-xs text-gray-500">
                Após criar o novo Colaborador, use o painel "Resolver Duplicados" para mesclar e migrar lançamentos.
              </p>
              <div className="flex justify-end gap-2 pt-2">
                <button onClick={() => setSimilarAlert(null)}
                  className="px-4 py-2 bg-gray-700 text-white rounded-lg text-sm">
                  Cancelar
                </button>
                <button
                  onClick={async () => {
                    // Re-submete com bypass=true
                    const payload = { ...similarAlert.payload, bypass_similar_alert: true };
                    setSaving(true);
                    try {
                      const res = await fetch(`${API_URL}/api/colaboradores`, {
                        method: 'POST',
                        headers: authHeaders({ 'Content-Type': 'application/json' }),
                        body: JSON.stringify(payload),
                      });
                      if (res.ok) {
                        toast.success('Colaborador cadastrado (alerta ignorado)');
                        setSimilarAlert(null);
                        setShowModal(false);
                        await fetchColabs();
                      } else {
                        const e = await res.json(); toast.error(e.detail || 'Erro');
                      }
                    } finally { setSaving(false); }
                  }}
                  data-testid="similar-alert-bypass"
                  className="px-4 py-2 bg-amber-500 hover:bg-amber-400 text-black font-semibold rounded-lg text-sm">
                  Criar mesmo assim
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Bloco 9 Correção 3 — Painel de resolução de duplicados */}
      {showDupPanel && (
        <DuplicatePanelModal onClose={() => setShowDupPanel(false)}
          onMerged={() => fetchColabs()} />
      )}
    </div>
  );
}


// ============================================================
// Bloco 9 Correção 3 (Fev/2026) — Painel de resolução de duplicados
// ============================================================
function DuplicatePanelModal({ onClose, onMerged }) {
  const [items, setItems] = useState([]);
  const [pendentesReparo, setPendentesReparo] = useState([]);
  const [loading, setLoading] = useState(false);
  const [confirmingId, setConfirmingId] = useState(null);
  const [autoMerging, setAutoMerging] = useState(false);
  const [reparing, setReparing] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const [r, rv] = await Promise.all([
        fetch(`${API_URL}/api/colaboradores/duplicados-operador?min_score=75`,
          { headers: authHeaders() }),
        fetch(`${API_URL}/api/colaboradores/duplicados-operador/vinculados-inconsistentes`,
          { headers: authHeaders() }),
      ]);
      const d = await r.json();
      const dv = await rv.json();
      setItems(d.items || []);
      setPendentesReparo(dv.items || []);
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const repararVinculados = async () => {
    if (!window.confirm(
      `Completar a migração de ${pendentesReparo.length} par(es) já vinculados ` +
      `mas com CC do operador ainda ativa?\n\n` +
      `Esses pares ficaram pela metade em merges anteriores. ` +
      `Os lançamentos serão migrados para o Colaborador e a CC do Operador será desativada.`
    )) return;
    setReparing(true);
    try {
      const r = await fetch(`${API_URL}/api/colaboradores/duplicados-operador/reparar-vinculados`, {
        method: 'POST',
        headers: authHeaders({ 'Content-Type': 'application/json' }),
      });
      const d = await r.json();
      if (r.ok) {
        toast.success(
          `Reparados: ${d.repaired} par(es). ` +
          `${d.total_lancamentos_migrados} lançamento(s) migrado(s). ` +
          `${d.ccs_soft_deleted_count} CC(s) de operador desativada(s).`
        );
        onMerged && onMerged();
        load();
      } else {
        toast.error(d.detail || 'Erro na reparação');
      }
    } finally { setReparing(false); }
  };

  const confirmar = async (p) => {
    if (!window.confirm(
      `Confirmar mesclagem?\n\n${p.lancamentos_a_migrar} lançamento(s) serão migrados ` +
      `do Operador para o Colaborador.\n\nSaldo combinado previsto: R$ ${p.saldo_combinado_previsto}`
    )) return;
    setConfirmingId(p.operator_id);
    try {
      const r = await fetch(`${API_URL}/api/colaboradores/duplicados-operador/confirmar`, {
        method: 'POST',
        headers: authHeaders({ 'Content-Type': 'application/json' }),
        body: JSON.stringify({ colaborador_id: p.colaborador_id, operator_id: p.operator_id }),
      });
      const d = await r.json();
      if (r.ok && d.ok) {
        toast.success(`Mesclado: ${d.lancamentos_migrados} lançamento(s) migrado(s).`);
        onMerged && onMerged();
        load();
      } else {
        toast.error(d.error || d.detail || 'Erro');
      }
    } finally { setConfirmingId(null); }
  };

  const autoMerge = async (minScore) => {
    const safePairs = items.filter(p => p.score >= minScore);
    if (safePairs.length === 0) {
      const total = items.length;
      if (total === 0) {
        toast.success('✓ Todos os duplicados já foram resolvidos. Nada a mesclar.');
      } else {
        const max = Math.max(...items.map(p => p.score));
        toast.info(
          `Nenhum par com score ≥ ${minScore}%. ` +
          `O par mais semelhante tem ${max}% — abaixo do limite seguro de auto-merge. ` +
          `Use o botão "Mesclar" individual ou "Ignorar" se for falso positivo.`
        );
      }
      return;
    }
    const label = minScore === 100 ? 'CPF idêntico (100%)' : `Score ≥ ${minScore}%`;
    if (!window.confirm(
      `Mesclar AUTOMATICAMENTE ${safePairs.length} par(es) com ${label}?\n\n` +
      `Todos os lançamentos serão migrados para o Colaborador. Operação atômica.`
    )) return;
    setAutoMerging(true);
    try {
      const r = await fetch(`${API_URL}/api/colaboradores/duplicados-operador/auto-merge`, {
        method: 'POST',
        headers: authHeaders({ 'Content-Type': 'application/json' }),
        body: JSON.stringify({ min_score: minScore }),
      });
      const d = await r.json();
      if (r.ok) {
        toast.success(`${d.merged} par(es) mesclado(s). ${d.skipped > 0 ? `${d.skipped} ignorado(s).` : ''}`);
        onMerged && onMerged();
        load();
      } else {
        toast.error(d.detail || 'Erro no auto-merge');
      }
    } finally { setAutoMerging(false); }
  };

  const ignorarPar = async (p) => {
    const motivo = window.prompt(
      `Marcar este par como FALSO POSITIVO (não é a mesma pessoa)?\n\n` +
      `Colaborador: ${p.colaborador_nome}\nOperador: ${p.operator_name}\n\n` +
      `Opcional: explique o motivo (ex: "placeholder do sistema", "homônimos diferentes").`,
      ''
    );
    if (motivo === null) return; // cancelou
    try {
      const r = await fetch(`${API_URL}/api/colaboradores/duplicados-operador/ignorar`, {
        method: 'POST',
        headers: authHeaders({ 'Content-Type': 'application/json' }),
        body: JSON.stringify({
          colaborador_id: p.colaborador_id, operator_id: p.operator_id, motivo,
        }),
      });
      if (r.ok) {
        toast.success('Par marcado como não-duplicado. Não aparecerá mais aqui.');
        load();
      } else {
        const d = await r.json();
        toast.error(d.detail || 'Erro ao ignorar');
      }
    } catch {
      toast.error('Erro de conexão');
    }
  };

  return (
    <div className="fixed inset-0 bg-black/70 z-[55] flex items-center justify-center p-4">
      <div className="bg-[#1A1A1A] border border-gray-800 rounded-xl max-w-5xl w-full max-h-[90vh] overflow-y-auto"
           data-testid="duplicate-panel-modal">
        <div className="px-4 py-3 border-b border-gray-800 flex items-center justify-between sticky top-0 bg-[#1A1A1A] z-10">
          <h3 className="font-semibold text-white">Resolver Duplicados Colaborador × Operador</h3>
          <button onClick={onClose} className="text-gray-400 hover:text-white">×</button>
        </div>
        <div className="p-4 space-y-4">
          <div className="bg-blue-500/10 border border-blue-500/30 text-blue-200 rounded-lg p-3 text-xs">
            Pares com nome ou CPF idênticos/similares. Ao confirmar, os lançamentos da Conta Corrente do
            Operador serão migrados para a CC do Colaborador (registro central) e o vínculo será criado.
            <strong className="block mt-1">Nenhum lançamento é perdido.</strong>
          </div>

          {pendentesReparo.length > 0 && (
            <div className="bg-orange-500/10 border border-orange-500/40 rounded-lg p-4 space-y-3"
                 data-testid="reparo-banner">
              <div className="flex items-start gap-3">
                <div className="flex-1">
                  <h4 className="font-semibold text-orange-200 text-sm">
                    ⚠ {pendentesReparo.length} par(es) vinculado(s) com CC duplicada
                  </h4>
                  <p className="text-xs text-gray-200 mt-1">
                    Estes pares já estão vinculados (Operador ↔ Colaborador), mas a CC do Operador
                    ainda existe com lançamentos não migrados. Resultado de merges anteriores que
                    ficaram pela metade. Clique em <strong>Reparar</strong> para completar a migração.
                  </p>
                </div>
                <button
                  onClick={repararVinculados}
                  disabled={reparing}
                  data-testid="reparar-vinculados-button"
                  className="px-3 py-2 bg-orange-500 hover:bg-orange-400 text-black font-semibold rounded text-xs whitespace-nowrap disabled:opacity-50"
                >
                  {reparing ? 'Reparando...' : '🛠 Reparar tudo'}
                </button>
              </div>
              <div className="overflow-x-auto border border-orange-500/30 rounded">
                <table className="w-full text-[11px]">
                  <thead className="bg-black/60 text-orange-100 uppercase">
                    <tr>
                      <th className="text-left px-2 py-1.5 font-semibold">Colaborador</th>
                      <th className="text-left px-2 py-1.5 font-semibold">Operador (CC pendente)</th>
                      <th className="text-right px-2 py-1.5 font-semibold">Lancs</th>
                      <th className="text-right px-2 py-1.5 font-semibold">Saldo operador</th>
                    </tr>
                  </thead>
                  <tbody>
                    {pendentesReparo.map(p => (
                      <tr key={`rep-${p.operator_id}`} className="border-t border-orange-500/20 bg-black/30">
                        <td className="px-2 py-1.5 text-white">{p.colaborador_nome}</td>
                        <td className="px-2 py-1.5 text-white">{p.operator_name}</td>
                        <td className="px-2 py-1.5 text-right font-mono text-white">{p.lancamentos_nao_migrados}</td>
                        <td className="px-2 py-1.5 text-right font-mono text-white">
                          R$ {(p.saldo_operador_pendente || 0).toFixed(2)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
          {items.length > 0 && (
            <div className="flex flex-wrap items-center gap-2 p-3 bg-[#0A0A0A] border border-gray-800 rounded-lg">
              <span className="text-xs text-gray-400 mr-2">Mesclar em massa:</span>
              <button
                onClick={() => autoMerge(100)}
                disabled={autoMerging}
                data-testid="auto-merge-100-button"
                className="px-3 py-1.5 bg-emerald-500/10 hover:bg-emerald-500/20 text-emerald-300 border border-emerald-500/30 rounded text-xs disabled:opacity-50"
              >
                {autoMerging ? '...' : '✓ Mesclar todos com CPF idêntico (100%)'}
              </button>
              <button
                onClick={() => autoMerge(90)}
                disabled={autoMerging}
                data-testid="auto-merge-90-button"
                className="px-3 py-1.5 bg-amber-500/10 hover:bg-amber-500/20 text-amber-300 border border-amber-500/30 rounded text-xs disabled:opacity-50"
              >
                {autoMerging ? '...' : '⚠ Mesclar todos com nome idêntico (≥ 90%)'}
              </button>
            </div>
          )}
          {loading && <p className="text-gray-400 text-center py-4">Carregando...</p>}
          {!loading && items.length === 0 && (
            <p className="text-center py-8 text-gray-500 text-sm" data-testid="dup-panel-empty">
              ✓ Nenhum par duplicado detectado.
            </p>
          )}
          {items.length > 0 && (
            <div className="overflow-x-auto border border-gray-800 rounded-lg">
              <table className="w-full text-xs" data-testid="dup-panel-tabela">
                <thead className="bg-[#0A0A0A] text-gray-400 uppercase">
                  <tr>
                    <th className="text-left px-3 py-2">Colaborador</th>
                    <th className="text-left px-3 py-2">Operador</th>
                    <th className="text-right px-3 py-2">Score</th>
                    <th className="text-right px-3 py-2">Lancs migrar</th>
                    <th className="text-right px-3 py-2">Saldo combinado</th>
                    <th className="text-right px-3 py-2">Ações</th>
                  </tr>
                </thead>
                <tbody>
                  {items.map(p => (
                    <tr key={`${p.colaborador_id}-${p.operator_id}`}
                        className="border-t border-gray-800 hover:bg-gray-800/30">
                      <td className="px-3 py-2 text-gray-300">
                        {p.colaborador_nome}
                        <div className="text-[10px] text-gray-500 font-mono">{p.colaborador_cpf || '—'}</div>
                      </td>
                      <td className="px-3 py-2 text-gray-300">
                        {p.operator_name}
                        <div className="text-[10px] text-gray-500 font-mono">{p.operator_document || '—'}</div>
                      </td>
                      <td className={`px-3 py-2 text-right font-mono font-bold ${
                        p.score >= 90 ? 'text-rose-400' : 'text-amber-400'
                      }`}>{p.score}%</td>
                      <td className="px-3 py-2 text-right text-gray-300 font-mono">{p.lancamentos_a_migrar}</td>
                      <td className="px-3 py-2 text-right font-mono text-gray-300">R$ {p.saldo_combinado_previsto}</td>
                      <td className="px-3 py-2 text-right">
                        <div className="flex items-center justify-end gap-1">
                          <button onClick={() => confirmar(p)}
                            disabled={confirmingId === p.operator_id}
                            data-testid={`dup-merge-${p.operator_id}`}
                            className="px-2 py-1 bg-emerald-500/10 hover:bg-emerald-500/20 text-emerald-300 border border-emerald-500/30 rounded text-xs disabled:opacity-50">
                            {confirmingId === p.operator_id ? '...' : 'Mesclar'}
                          </button>
                          <button onClick={() => ignorarPar(p)}
                            data-testid={`dup-ignore-${p.operator_id}`}
                            title="Marcar como falso positivo — par não voltará a aparecer"
                            className="px-2 py-1 bg-gray-500/10 hover:bg-gray-500/20 text-gray-300 border border-gray-500/30 rounded text-xs">
                            Ignorar
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
